/**
 * `WorkspaceFilePaneShell` — Claude-web-style page-level mount of the
 * `WorkspaceFilePane`. Sits as a sibling of the App shell's main
 * column + RightSidebar so the viewer occupies the FULL VIEWPORT
 * HEIGHT (not nested under `CaseHeaderBar` + breadcrumbs) and the
 * push-aside animation operates at page scope.
 *
 * Behaviour:
 *   - When `useWorkspaceFileStore.activeFile` is `null`: returns null,
 *     no DOM nodes.
 *   - When `activeFile` is set: renders the `WorkspaceFilePane` inside
 *     a flex sibling that animates its width from 0 to a wide target
 *     (640px / 50vw / 50% of remaining shell space, whichever fits) so
 *     the chat + sidebars shrink to share the left half of the page.
 *
 * The animation:
 *   - CSS `transition` on `width` + `min-width` so the open/close
 *     animates smoothly without fighting react-resizable-panels.
 *   - We use a mounted/measured pattern: mount immediately at width 0,
 *     then on a microtask switch to the target width so the browser
 *     can interpolate. Close is the reverse — width 0 + an `afterClose`
 *     timer unmounts after the transition completes.
 *
 * Width:
 *   - Default 50vw, clamped to [560px, 960px] so the chat stays usable
 *     on narrow viewports + the viewer doesn't sprawl on ultra-wide.
 *
 * Mobile/compact:
 *   - Renders as a full-bleed fixed overlay (z-50) so the chat isn't
 *     squeezed below its 320px minimum.
 *
 * Close + URL sync: the WorkspaceFilePane's topbar `×` button + Esc
 * call `closeFile()` (the store method). The case workspace's
 * `useEffect` watching `activeFile` (in `CaseWorkspaceLayout.tsx`)
 * handles URL strip on the store→null transition.
 */

import { useEffect, useRef, useState } from "react";
import {
  useWorkspaceFileStore,
  type FileRef,
} from "@/stores/useWorkspaceFileStore";
import { useActiveContextStore } from "@/stores/useActiveContextStore";
import { WorkspaceFilePane } from "@/components/workspace/WorkspaceFilePane";
import { isCompactViewport, useViewportSize } from "@/hooks/useViewportSize";

// Animation timing — kept in lockstep with the CSS transition duration
// below so the post-close unmount fires exactly when the slide-out ends.
const OPEN_CLOSE_MS = 220;

export function WorkspaceFilePaneShell() {
  const activeFile = useWorkspaceFileStore((s) => s.activeFile);
  const closeFile = useWorkspaceFileStore((s) => s.closeFile);
  const viewport = useViewportSize();
  const isCompact = isCompactViewport(viewport);

  // A FileRef is CASE-RELATIVE and every viewer resolves it against the
  // CURRENT selection (`useFileBytes` keys on `selection.caseId`), so a ref
  // must never outlive the case it was minted under. `WorkspaceFileUrlSync`
  // closes on URL navigation — but a selection change WITHOUT a navigation
  // (programmatic `selectCase`, the `window.__acsStore` accessor) left the
  // viewer mounted, re-reading the old case's path against the new case:
  // recurring NOT_FOUND on every gateway reconnect, forensicated runtime-side
  // by `lexiun-agent-specialists` 2026-08-20 (bus `…-j3nw`). Close on any
  // case→different-case transition; null edges (cold load, org scope) are
  // the URL sync's territory and stay untouched.
  const activeCaseId = useActiveContextStore((st) => st.selection?.caseId ?? null);
  const prevCaseIdRef = useRef<string | null>(activeCaseId);
  useEffect(() => {
    const prev = prevCaseIdRef.current;
    prevCaseIdRef.current = activeCaseId;
    if (
      prev !== null &&
      activeCaseId !== null &&
      prev !== activeCaseId &&
      useWorkspaceFileStore.getState().activeFile
    ) {
      closeFile();
    }
  }, [activeCaseId, closeFile]);

  // `keepMountedFile` holds the file ref across the close animation so
  // the user sees the slide-out (rather than an instant unmount). On
  // open: we mount immediately, then trigger the width transition via
  // `isOpen`. On close: we flip `isOpen` false (slide out), then
  // after OPEN_CLOSE_MS clear `keepMountedFile`.
  const [keepMountedFile, setKeepMountedFile] = useState<FileRef | null>(
    activeFile,
  );
  const [isOpen, setIsOpen] = useState<boolean>(activeFile !== null);

  useEffect(() => {
    if (activeFile) {
      // Open: mount + flip open on the next paint so the browser can
      // interpolate the width from 0 → target.
      setKeepMountedFile(activeFile);
      const id = requestAnimationFrame(() => setIsOpen(true));
      return () => cancelAnimationFrame(id);
    }
    // Close: trigger the slide-out, then unmount after the transition.
    setIsOpen(false);
    const id = window.setTimeout(() => {
      setKeepMountedFile(null);
    }, OPEN_CLOSE_MS);
    return () => window.clearTimeout(id);
  }, [activeFile]);

  if (!keepMountedFile) return null;

  // Compact viewports: full-bleed fixed overlay (the existing mobile/
  // tablet path from CaseWorkspaceLayout, now hoisted to shell level).
  if (isCompact) {
    return (
      <div
        data-testid="workspace-file-pane-overlay"
        className="fixed inset-0 z-50 flex flex-col bg-white dark:bg-slate-950"
      >
        <WorkspaceFilePane />
      </div>
    );
  }

  // Desktop: in-flow sibling that animates its width. The clamp keeps
  // the chat usable on narrower viewports + prevents the viewer from
  // sprawling on ultra-wide. `min-width: 0` lets flex children of the
  // outer App shell shrink without overflow.
  return (
    <aside
      data-testid="workspace-file-pane-shell"
      data-state={isOpen ? "open" : "closed"}
      style={{
        width: isOpen ? "min(50vw, 960px)" : 0,
        minWidth: isOpen ? "min(560px, 50vw)" : 0,
      }}
      className="relative h-full shrink-0 overflow-hidden border-l border-cream-200/60 bg-white transition-[width,min-width] duration-200 ease-out dark:border-coffee-700/60 dark:bg-coffee-950"
    >
      <WorkspaceFilePane />
    </aside>
  );
}
